/**
 * The activity-category catalog (RESP2d).
 *
 * ONE endpoint, read by every surface that has to offer or name an area
 * of work: both form modals, both detail pages, the workspace cards and
 * the entity overviews.
 *
 * Deliberately NOT `fetchResponsibilityAreas`. That answer carries the
 * default assignee, the participants and three usage counters per area —
 * fine on the settings page it was built for, and far too much to ship
 * to every task list in the product. This one is seven fields.
 *
 * Same conventions as the rest of ./: raw fetch, Bearer headers from
 * ./core, a thrown Error carrying the German message and `err.status`.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

async function _throwFrom(res) {
  const err = new Error(await parseAdminErrorResponse(res));
  err.status = res.status;
  throw err;
}

/**
 * GET /api/admin/activity-categories/options → `{items, total}`.
 *
 * Returns EVERY area of the organisation, retired ones included. That
 * is not an oversight: a form must not offer an inactive area, and a
 * list must still be able to name one, and only the caller knows which
 * of those two it is doing. Filtering server-side would make a task
 * from last month render as an unknown area the moment somebody
 * switched its category off.
 */
export async function fetchActivityCategoryOptions() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/activity-categories/options`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}
